import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MessageList } from '@/components/message-list';
import { MessageView } from '@/components/message-view';
import { getUnifiedInbox, searchMessages, sweepInbox, syncAll } from '@/api/inbox';
import { bulkDeleteMessages, bulkMarkSpam, bulkMarkRead } from '@/api/messages';
import { selectionKey, groupSelection } from '@/lib/bulk-selection';
import { ShieldAlert, Eye, Inbox as InboxIcon, ArrowLeft, Search, X, CheckSquare, Square, Trash2, ShieldCheck, ShieldOff, Loader2, Sparkles, RefreshCw, MailOpen } from 'lucide-react';
import { SweepModal } from '@/components/sweep-modal';
import { useUi } from '@/components/ui-dialogs';
import { useMailStore } from '@/stores/mail';
import { SenderAvatar } from '@/components/sender-avatar';

type View = 'personal' | 'all' | 'review' | 'spam';

const VIEW_META: Record<View, { title: string; icon: React.ReactNode; desc: string }> = {
  personal: { title: 'Personal', icon: <InboxIcon size={20} />, desc: 'All personal mailboxes' },
  all: { title: 'Unified Inbox', icon: <InboxIcon size={20} />, desc: 'All mailboxes, all categories' },
  review: { title: 'Might be Spam', icon: <Eye size={20} />, desc: 'Borderline messages — Rspamd flagged but not confirmed spam' },
  spam: { title: 'Spam', icon: <ShieldAlert size={20} />, desc: 'Messages identified as spam' }
};

interface UnifiedInboxProps {
  view: View;
}

export function UnifiedInboxRoute({ view }: UnifiedInboxProps) {
  const ui = useUi();
  const { mailboxes } = useMailStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Swipe-left on the open message (mobile) closes it back to the list.
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const [selectedUid, setSelectedUid] = useState<number | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [selectedMailboxId, setSelectedMailboxId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [showSweep, setShowSweep] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [lastShiftKey, setLastShiftKey] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  const validView: View = view;
  const meta = VIEW_META[validView];
  const isSearching = debouncedSearch.trim().length > 0;

  const { data, isLoading, error } = useQuery({
    queryKey: ['unified-inbox', validView, debouncedSearch],
    queryFn: () => isSearching ? searchMessages(debouncedSearch, validView) : getUnifiedInbox(validView)
  });

  const messages = data?.messages || [];

  // Keys carry the folder: a Junk uid and an INBOX uid are different messages (B-03).
  const msgKey = selectionKey;

  // Group selected by mailboxId for bulk operations, as {uid, folder} items
  const groupedByMailbox = () => groupSelection(selectedKeys);

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      const groups = groupedByMailbox();
      const results = await Promise.all(
        Object.entries(groups).map(([mailboxId, items]) => bulkDeleteMessages(mailboxId, items))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      setSelectedKeys(new Set());
      setSelectMode(false);
    }
  });

  const bulkSpamMutation = useMutation({
    mutationFn: async (isSpam: boolean) => {
      const groups = groupedByMailbox();
      const results = await Promise.all(
        Object.entries(groups).map(([mailboxId, items]) => bulkMarkSpam(mailboxId, items, isSpam))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      setSelectedKeys(new Set());
      setSelectMode(false);
    }
  });

  const bulkReadMutation = useMutation({
    mutationFn: async () => {
      const groups = groupedByMailbox();
      return Promise.all(Object.entries(groups).map(([mailboxId, items]) => bulkMarkRead(mailboxId, items, true)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      setSelectedKeys(new Set());
      setSelectMode(false);
    }
  });

  const refreshMutation = useMutation({
    mutationFn: () => syncAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    }
  });
  const sweepMutation = useMutation({
    mutationFn: () => sweepInbox(),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['unified-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      ui.toast(r.swept > 0
        ? { kind: 'success', title: 'Sweep complete', message: `${r.swept} message${r.swept === 1 ? '' : 's'} moved to Spam.` }
        : { kind: 'info', message: 'Nothing matched your filters — the inbox is already clean.' });
    }
  });

  const handleSelectMessage = (uid: number, mailboxId: string, folder?: string) => {
    setSelectedUid(uid);
    setSelectedMailboxId(mailboxId);
    setSelectedFolder(folder || null);
  };

  const toggleSelect = (key: string, shiftKey: boolean = false) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (shiftKey && lastShiftKey && lastShiftKey !== key) {
        const keys = messages.map(msgKey);
        const i1 = keys.indexOf(lastShiftKey);
        const i2 = keys.indexOf(key);
        if (i1 >= 0 && i2 >= 0) {
          const lo = Math.min(i1, i2), hi = Math.max(i1, i2);
          for (let i = lo; i <= hi; i++) next.add(keys[i]);
          setLastShiftKey(key);
          return next;
        }
      }
      if (next.has(key)) next.delete(key); else next.add(key);
      setLastShiftKey(key);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedKeys.size === 0) return;
    const n = selectedKeys.size;
    if (!(await ui.confirm({
      title: `Delete ${n} message${n === 1 ? '' : 's'}?`,
      body: 'They move to Recently Deleted and can still be restored from there.',
      confirmLabel: `Delete ${n}`, danger: true,
    }))) return;
    bulkDeleteMutation.mutate();
  };

  const handleBulkSpam = async (isSpam: boolean) => {
    if (selectedKeys.size === 0) return;
    const label = isSpam ? 'spam' : 'not spam';
    const count = selectedKeys.size;
    if (!(await ui.confirm({
      title: `Mark ${count} message${count === 1 ? '' : 's'} as ${label}?`,
      confirmLabel: `Mark as ${label}`,
    }))) return;
    bulkSpamMutation.mutate(isSpam);
  };

  const hasSelectedMessage = selectedUid !== null && selectedMailboxId !== null;
  const selCount = selectedKeys.size;
  const isPending = bulkDeleteMutation.isPending || bulkSpamMutation.isPending || bulkReadMutation.isPending;

  return (
    <div className="flex h-full w-full">
      {/* Message list */}
      <div className={`
        flex flex-col border-r border-bmail-border bg-bmail-surface
        ${hasSelectedMessage ? 'hidden md:flex md:w-1/3 md:min-w-[20rem] md:max-w-md' : 'flex w-full md:w-1/3 md:min-w-[20rem] md:max-w-md animate-slide-in-left'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-bmail-border px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-bmail-gold">{meta.icon}</span>
            <div>
              <h2 className="font-semibold text-bmail-text">{meta.title}</h2>
              <p className="text-xs text-bmail-text-secondary">{meta.desc}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              title="Check all mailboxes for new mail"
              className="flex items-center gap-1 rounded px-2 py-1.5 text-xs text-bmail-text-secondary transition-colors hover:bg-bmail-surface-hover hover:text-bmail-gold disabled:opacity-50"
            >
              <RefreshCw size={14} className={refreshMutation.isPending ? 'animate-spin' : ''} /> {refreshMutation.isPending ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              onClick={() => setShowSweep(true)}
              title="Sweep inbox by filter rules or unsubscribe"
              className="flex items-center gap-1 rounded px-2 py-1.5 text-xs text-bmail-text-secondary transition-colors hover:bg-bmail-surface-hover hover:text-bmail-gold"
            >
              <Sparkles size={14} /> Sweep
            </button>
            {messages.length > 0 && (
              <button
                onClick={() => { setSelectMode(!selectMode); setSelectedKeys(new Set()); }}
                className={`rounded p-1.5 transition-colors ${selectMode ? 'text-bmail-gold bg-bmail-surface-hover' : 'text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-gold'}`}
                title="Multi-select"
              >
                <CheckSquare size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2 border-b border-bmail-border px-4 py-2">
          <Search size={14} className="shrink-0 text-bmail-text-secondary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search subject, sender, body…"
            className="flex-1 bg-transparent text-sm text-bmail-text placeholder:text-bmail-text-secondary outline-none"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="shrink-0 rounded p-0.5 text-bmail-text-secondary hover:text-bmail-text" title="Clear search">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Search results action bar — delete all matching */}
        {isSearching && !selectMode && messages.length > 0 && (
          <div className="flex items-center gap-2 border-b border-bmail-border bg-bmail-error/5 px-4 py-2">
            <span className="text-xs text-bmail-text-secondary">{messages.length} results for "{debouncedSearch}"</span>
            <div className="flex-1" />
            <button
              onClick={async () => {
                // One clear dialog naming the count and the query, rather than
                // the two stacked confirms this used to ask for.
                if (!(await ui.confirm({
                  title: `Delete all ${messages.length} search results?`,
                  body: `Every message matching “${debouncedSearch}” will be deleted. They move to Recently Deleted, where they can still be restored.`,
                  confirmLabel: `Delete ${messages.length}`, danger: true,
                }))) return;
                setSelectedKeys(new Set(messages.map(msgKey)));
                setSelectMode(true);
                // Let the selection state settle before the mutation reads it.
                setTimeout(() => bulkDeleteMutation.mutate(), 100);
              }}
              disabled={isPending}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-bmail-error hover:bg-bmail-error/10 disabled:opacity-50"
              title="Delete all search results"
            >
              {isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete All
            </button>
          </div>
        )}

        {/* Action panel — appears when emails are selected */}
        {selectMode && selCount > 0 && (
          <div className="flex items-center gap-2 border-b border-bmail-border bg-bmail-goldfill/10 px-4 py-2 flex-wrap">
            <span className="text-xs font-medium text-bmail-gold">{selCount} selected</span>
            <div className="flex-1" />
            <button
              onClick={() => bulkReadMutation.mutate()}
              disabled={isPending}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-gold disabled:opacity-50"
              title="Mark selected as read"
            >
              <MailOpen size={14} /> Mark read
            </button>
            <button
              onClick={() => handleBulkSpam(false)}
              disabled={isPending}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-bmail-text-secondary hover:bg-bmail-surface-hover hover:text-bmail-gold disabled:opacity-50"
              title="Mark as not spam"
            >
              <ShieldCheck size={14} /> Not Spam
            </button>
            <button
              onClick={() => handleBulkSpam(true)}
              disabled={isPending}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-bmail-warning hover:bg-bmail-warning/10 disabled:opacity-50"
              title="Mark as spam"
            >
              <ShieldOff size={14} /> Spam
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={isPending}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-bmail-error hover:bg-bmail-error/10 disabled:opacity-50"
              title="Delete selected"
            >
              {isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
            </button>
            <button
              onClick={() => { setSelectedKeys(new Set()); setSelectMode(false); }}
              disabled={isPending}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-bmail-text-secondary hover:bg-bmail-surface-hover disabled:opacity-50"
              title="Cancel selection"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Select-all bar */}
        {selectMode && messages.length > 0 && selCount === 0 && (
          <div className="flex items-center gap-2 border-b border-bmail-border bg-bmail-surface/50 px-4 py-1.5 text-xs text-bmail-text-secondary">
            <button
              onClick={() => setSelectedKeys(new Set(messages.map(msgKey)))}
              className="flex items-center gap-1 hover:text-bmail-gold"
            >
              <Square size={14} /> Select all
            </button>
            <span>Click checkboxes to select · Shift+click for range</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-4 py-6 text-center text-sm text-bmail-error">
            Failed to load {meta.title}.
          </div>
        )}

        {/* Empty */}
        {!isLoading && messages.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-bmail-text-secondary">
            {validView === 'spam' && 'No spam detected. Nice!'}
            {validView === 'review' && 'Nothing to review — inbox is clean.'}
            {(validView === 'personal' || validView === 'all') && 'No messages yet. Try syncing a mailbox.'}
          </div>
        )}

        {/* Message list */}
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-3xl border-x border-bmail-border md:border-none">
          {messages.map((m) => {
            const key = msgKey(m);
            const isSelected = selectedKeys.has(key);
            return (
              <div
                key={key}
                className={`flex items-center border-b border-bmail-border px-2 transition-colors sm:px-3 ${
                  selectedUid !== null && selectedUid === m.uid && selectedMailboxId === m.mailboxId && (selectedFolder ?? 'INBOX') === (m.folder || 'INBOX') ? 'bg-bmail-surface-hover' : 'bg-transparent hover:bg-bmail-surface-hover'
                } ${!m.isRead ? 'font-bold text-bmail-text' : 'font-normal text-bmail-text-secondary'} ${selectMode && isSelected ? 'bg-bmail-goldfill/10' : ''}`}
              >
                {selectMode && (
                  <button
                    onClick={(e) => toggleSelect(key, e.shiftKey)}
                    className="flex shrink-0 items-center px-2 text-bmail-text-secondary hover:text-bmail-gold"
                  >
                    {isSelected ? <CheckSquare size={16} className="text-bmail-gold" /> : <Square size={16} />}
                  </button>
                )}
                <SenderAvatar name={m.fromName} email={m.fromEmail} size={36} />
                <button
                  onClick={() => handleSelectMessage(m.uid, m.mailboxId, m.folder)}
                  className="min-w-0 flex-1 px-3 py-3 text-left"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-[15px]">{m.fromName || m.fromEmail || 'Unknown'}</span>
                    <span className="shrink-0 text-xs text-bmail-text-secondary">
                      {m.date ? new Date(m.date * 1000).toLocaleDateString() : ''}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[14px]">{m.subject || '(no subject)'}</span>
                    {m.spamAction === 'spam' && <ShieldAlert size={14} className="shrink-0 text-bmail-error" />}
                    {m.spamAction === 'review' && <Eye size={14} className="shrink-0 text-bmail-warning" />}
                  </div>
                  <p className="mt-1 line-clamp-1 text-[13px] text-bmail-text-secondary">{m.snippet}</p>
                </button>
              </div>
            );
          })}
          </div>
        </div>
      </div>

      {/* Message view */}
      <div
        onTouchStart={(e) => { const t = e.touches[0]; touchStart.current = { x: t.clientX, y: t.clientY }; }}
        onTouchEnd={(e) => {
          const s = touchStart.current; touchStart.current = null;
          if (!s || !hasSelectedMessage || window.innerWidth >= 768) return;
          const t = e.changedTouches[0];
          const dx = t.clientX - s.x, dy = t.clientY - s.y;
          // Right swipe, horizontally dominant → back to the inbox list (iOS back gesture).
          if (dx > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) { setSelectedUid(null); setSelectedMailboxId(null); }
        }}
        className={`
        flex flex-col bg-[var(--bg)]
        ${hasSelectedMessage ? 'flex w-full md:flex-1 animate-slide-in-right' : 'hidden md:flex md:flex-1'}
      `}>
        {/* Mobile back button */}
        {hasSelectedMessage && (
          <button
            onClick={() => { setSelectedUid(null); setSelectedMailboxId(null); }}
            className="flex items-center gap-1 border-b border-bmail-border px-4 py-2 text-sm text-bmail-text-secondary hover:text-bmail-text md:hidden animate-slide-in-left"
          >
            <ArrowLeft size={16} /> Back to inbox
          </button>
        )}
        {hasSelectedMessage ? (
          <MessageView
            mailboxId={selectedMailboxId}
            uid={selectedUid}
            folder={selectedFolder}
            onActioned={() => { setSelectedUid(null); setSelectedMailboxId(null); setSelectedFolder(null); }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-bmail-text-secondary">
            <div className="text-center">
              <div className="mb-3 flex justify-center">{meta.icon}</div>
              <p className="text-sm">Select a message to read</p>
            </div>
          </div>
        )}
      </div>
      {showSweep && (
        <SweepModal
          mailboxes={mailboxes}
          onClose={() => setShowSweep(false)}
        />
      )}
    </div>
  );
}